import { PinnedRowProps } from '@app/types/main-table';
import { memo } from 'react';
import { TableRow } from '@mui/material';

import MainTableCellBasic from '@components/MainTable/MainTableCellBasic';
import styles from './index.module.scss';

function MainTablePinnedRowComponent<TData extends Record<string, any>>({
  row,
  isLeftScrollable,
  isRightScrollable,
}: PinnedRowProps<TData>) {
  const isTopPinned = row.getIsPinned() === 'top';
  const isBottomPinned = row.getIsPinned() === 'bottom';

  const pinnedRowClasses = [
    styles.pinnedRow,
    isTopPinned && styles.topPinned,
    isBottomPinned && styles.bottomPinned,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TableRow className={pinnedRowClasses}>
      {row.getVisibleCells().map((cell) => {
        return (
          <MainTableCellBasic
            key={cell.id}
            cell={cell}
            isLeftScrollable={isLeftScrollable}
            isRightScrollable={isRightScrollable}
          />
        );
      })}
    </TableRow>
  );
}

// Custom comparison function for MainTablePinnedRow memo
const areEqual = <TData extends Record<string, any>>(
  prevProps: PinnedRowProps<TData>,
  nextProps: PinnedRowProps<TData>
) => {
  // Compare row ID (unique identifier)
  if (prevProps.row.id !== nextProps.row.id) {
    return false;
  }

  // Compare pin state
  if (prevProps.row.getIsPinned() !== nextProps.row.getIsPinned()) {
    return false;
  }

  // Compare scroll states
  if (
    prevProps.isLeftScrollable !== nextProps.isLeftScrollable ||
    prevProps.isRightScrollable !== nextProps.isRightScrollable
  ) {
    return false;
  }

  // Compare table instance by reference
  if (prevProps.table !== nextProps.table) {
    return false;
  }

  // Compare visible cells count and IDs
  const prevCells = prevProps.row.getVisibleCells();
  const nextCells = nextProps.row.getVisibleCells();
  if (prevCells.length !== nextCells.length) {
    return false;
  }

  // Compare cell IDs to detect if cells changed
  for (let i = 0; i < prevCells.length; i++) {
    if (prevCells[i].id !== nextCells[i].id) {
      return false;
    }
  }

  // All checks passed - props are equal, skip re-render
  return true;
};

const MainTablePinnedRow = memo(
  MainTablePinnedRowComponent,
  areEqual
) as typeof MainTablePinnedRowComponent;

export default MainTablePinnedRow;
